import { persistAuthState, readStoredAuthToken, readStoredPermission } from "./authTokens";
import { readStoredAuthCredentials } from "./authCredentials";
import { normalizeAuthApiResponse } from "./authResponse";

type ParkingBoxApiPayload = {
  action: string;
} & Record<string, unknown>;

type ParkingBoxApiResult = {
  status: number;
  body: string;
  json: unknown | null;
};

type ParkingBoxApiOptions = {
  jwtToken?: string | null;
  useStoredToken?: boolean;
  retryOnExpiredToken?: boolean;
};

type RequestHeaders = Record<string, string>;

type ApiRequestArgs = {
  apiUrl: string;
  payload: ParkingBoxApiPayload;
  headers: RequestHeaders;
};

const EXPIRED_TOKEN_TEXT = "error: expired token";
const BAD_TOKEN_TEXT = "error: bad token";

let refreshPromise: Promise<string | null> | null = null;

export async function callParkingBoxApi(
  payload: ParkingBoxApiPayload,
  options: ParkingBoxApiOptions = {},
): Promise<ParkingBoxApiResult> {
  const apiUrl = process.env.REACT_APP_PARKINGBOX_API_URL;

  if (!apiUrl) {
    throw new Error("Brak konfiguracji REACT_APP_PARKINGBOX_API_URL w pliku .env");
  }

  const requestHeaders: RequestHeaders = {
    "Content-Type": "application/json",
  };

  let resolvedJwt = options.jwtToken ?? null;

  if (!resolvedJwt && options.useStoredToken) {
    resolvedJwt = readStoredAuthToken();
  }

  if (options.useStoredToken && !resolvedJwt) {
    throw new Error("Brak tokenu uwierzytelniającego. Zaloguj się ponownie.");
  }

  if (resolvedJwt) {
    requestHeaders.Authorization = `Bearer ${resolvedJwt}`;
  }

  const performRequest = async (label?: string) => {
    const prefix = label ? `[ParkingBox API] ${label}` : "[ParkingBox API]";
    console.log(`${prefix} POST:`, apiUrl);
    console.log(`${prefix} Payload:`, payload);
    console.log(`${prefix} Headers:`, requestHeaders);

    return executeApiRequest({
      apiUrl,
      payload,
      headers: requestHeaders,
    });
  };

  const shouldRetryOnExpiredToken = options.retryOnExpiredToken !== false;

  const response = await performRequest();

  if (shouldRetryOnExpiredToken && hasExpiredTokenFlag(response.json)) {
    console.warn("[ParkingBox API] Received expired token response. Attempting automatic re-login.");
    const nextToken = await refreshAuthToken(apiUrl);
    if (!nextToken) {
      throw new Error(
        "Sesja wygasła i nie udało się przeprowadzić automatycznego logowania. Zaloguj się ponownie.",
      );
    }

    requestHeaders.Authorization = `Bearer ${nextToken}`;
    const retryResponse = await performRequest("Retry");

    if (hasExpiredTokenFlag(retryResponse.json)) {
      throw new Error(
        "Automatyczne logowanie nie powiodło się, ponieważ serwer ponownie zwrócił błąd wygasłego tokenu. Zaloguj się ponownie.",
      );
    }

    return retryResponse;
  }

  return response;
}

function hasExpiredTokenFlag(payload: unknown, depth = 0): boolean {
  if (!payload || typeof payload !== "object" || depth > 3) {
    if (Array.isArray(payload) && depth <= 3) {
      return payload.some((entry) => hasExpiredTokenFlag(entry, depth + 1));
    }
    return false;
  }

  if (Array.isArray(payload)) {
    return payload.some((entry) => hasExpiredTokenFlag(entry, depth + 1));
  }

  const record = payload as Record<string, unknown>;
  const status = typeof record.status === "number" ? record.status : null;
  const message =
    typeof record.message === "string" ? record.message.trim().toLowerCase() : null;

  if (
    message &&
    ((status === 2 && message.includes(EXPIRED_TOKEN_TEXT)) ||
      message.includes(BAD_TOKEN_TEXT))
  ) {
    return true;
  }

  const nestedSources = [record.data, record["_data_"], record.response, record.body];
  return nestedSources.some((source) => hasExpiredTokenFlag(source, depth + 1));
}

async function executeApiRequest(args: ApiRequestArgs): Promise<ParkingBoxApiResult> {
  const { apiUrl, payload, headers } = args;

  const response = await fetch(apiUrl, {
    method: "POST",
    cache: "no-store",
    headers,
    body: JSON.stringify(payload),
  });

  const body = await response.text();
  let parsed: unknown | null = null;

  try {
    parsed = body ? JSON.parse(body) : null;
  } catch {
    parsed = null;
  }

  console.log("[ParkingBox API] Status:", response.status);
  console.log("[ParkingBox API] Body:", body);
  if (parsed !== null) {
    console.log("[ParkingBox API] Parsed JSON:", parsed);
  }

  if (!response.ok) {
    throw new Error(
      `ParkingBox ERROR ${response.status}.`,
    );
  }

  return { status: response.status, body, json: parsed };
}

async function refreshAuthToken(apiUrl: string): Promise<string | null> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const credentials = readStoredAuthCredentials();
    if (!credentials) {
      console.warn(
        "[ParkingBox API] Nie znaleziono zapisanych danych logowania. Automatyczne logowanie nie będzie możliwe.",
      );
      return null;
    }

    try {
      const response = await executeApiRequest({
        apiUrl,
        payload: {
          action: "login",
          username: credentials.username,
          password: credentials.password,
        },
        headers: {
          "Content-Type": "application/json",
        },
      });

      const normalized = normalizeAuthApiResponse(response.json);
      const jwtToken = normalized?.token ?? null;
      const statusFlag = normalized?.status ?? null;
      const permissionLevel = normalized?.permission ?? readStoredPermission();
      const userId = normalized?.userId ?? null;

      if (!jwtToken || statusFlag !== 1) {
        console.warn(
          "[ParkingBox API] Automatyczne logowanie nie zwróciło prawidłowego tokenu.",
        );
        return null;
      }

      persistAuthState(jwtToken, credentials.remember, permissionLevel, userId);
      console.info("[ParkingBox API] Automatyczne logowanie zakończone powodzeniem.");
      return jwtToken;
    } catch (error) {
      console.error("[ParkingBox API] Nie udało się wykonać automatycznego logowania:", error);
      return null;
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}
